import { ApolloError } from "@apollo/client";
import { useCallback } from "react";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import { usePutAnswerForMe } from "src/generated/hooks/form-service/queries";
import { useRemoteDataMutation } from "src/hooks/useRemoteDataMutation";
import useRankedSchools from "src/hooks/useSchoolRank";
import { triggerRuleExecutionsForSoftDeletedOffers } from "src/services/form/offer";
import { RankedSchool } from "src/services/formTemplate/preRankingSection";
import * as AF from "src/types/formTemplate";
import * as GQL from "src/types/graphql";
import * as RD from "src/types/remoteData";
import { INSERT_FORM_SCHOOLS_RANK } from "../graphql/mutations";
import { deletedRankedSchoolsVar } from "../store";

type Props = {
  formId: string;
  schoolRanksRemoteData: RD.RemoteData<ApolloError, GQL.GetSchoolsRank>;
  schoolRankingSection: AF.Section<AF.WithId> | undefined;
  refetchSchoolRanks: () => Promise<unknown>;
};
export const useDeleteRankedSchools = ({
  formId,
  schoolRanksRemoteData,
  schoolRankingSection,
  refetchSchoolRanks,
}: Props) => {
  const flags = useFlags(["form-service-form-answers"]);
  const {
    getDeletedRanks,
    getUpsertedRanks,
    getDeletedOffers,
    getDeletedWaitlists,
  } = useRankedSchools([]);

  const [insertSchoolRank] = useRemoteDataMutation<
    GQL.InsertFormSchoolsRank,
    GQL.InsertFormSchoolsRankVariables
  >(INSERT_FORM_SCHOOLS_RANK);

  const { mutateAsync: putAnswerForMe } = usePutAnswerForMe(undefined, {
    onSuccess: (data) => {
      if ("error" in data) throw new Error(data.error?.error.message);
    },
  });

  // When grades change, we need to delete the schools that are no longer eligible, this is a workaround
  // for a weird bug where the useRankedSchools hook doesn't show the correct state after the parent navigate
  // from schoolRanksStep to preRankingStep.
  const getDeleteSchoolsAfterGradesChange = useCallback(
    (schoolsToRemove: uuid[]): GQL.form_school_rank_bool_exp => ({
      _or: schoolsToRemove.map((schoolId) => ({
        form_id: { _eq: formId },
        schools_ranking_section_id: { _eq: schoolRankingSection?.id },
        school_id: { _eq: schoolId },
      })),
    }),
    [formId, schoolRankingSection?.id]
  );

  // Same workaround as above: the useRankedSchools baseline is empty here, so
  // getDeletedOffers/getDeletedWaitlists would produce empty `_or` clauses that
  // match nothing, leaving offers/waitlists for the removed schools orphaned.
  // Build the where-clauses directly from schoolsToRemove instead.
  const getDeleteOffersAfterEligibilityChange = useCallback(
    (schoolsToRemove: uuid[]): GQL.offer_bool_exp => ({
      _or: schoolsToRemove.map((schoolId) => ({
        deleted_at: { _is_null: true },
        form_id: { _eq: formId },
        school_id: { _eq: schoolId },
      })),
    }),
    [formId]
  );

  const getDeleteWaitlistsAfterEligibilityChange = useCallback(
    (schoolsToRemove: uuid[]): GQL.waitlist_bool_exp => ({
      _or: schoolsToRemove.map((schoolId) => ({
        deleted_at: { _is_null: true },
        form_id: { _eq: formId },
        school_id: { _eq: schoolId },
      })),
    }),
    [formId]
  );

  const setSchoolRanks = useCallback(
    async (schoolIds: string[], schoolsToRemove?: string[]) => {
      if (!schoolRankingSection) return;

      const upsertedSchoolRanks = getUpsertedRanks(
        schoolIds.map((schoolId, rank) => ({
          form_id: formId,
          schools_ranking_section_id: schoolRankingSection.id,
          school_id: schoolId,
        }))
      );

      const deletedSchoolRanks = schoolsToRemove
        ? getDeleteSchoolsAfterGradesChange(schoolsToRemove)
        : getDeletedRanks(upsertedSchoolRanks);

      const deletedOffers = schoolsToRemove
        ? getDeleteOffersAfterEligibilityChange(schoolsToRemove)
        : getDeletedOffers(upsertedSchoolRanks);
      const deletedWaitlists = schoolsToRemove
        ? getDeleteWaitlistsAfterEligibilityChange(schoolsToRemove)
        : getDeletedWaitlists(upsertedSchoolRanks);

      if (flags["form-service-form-answers"].enabled) {
        await putAnswerForMe({
          path: {
            formId: formId,
            questionId: schoolRankingSection.id,
          },
          body: {
            data: upsertedSchoolRanks.map((schoolRank, index) => ({
              schoolId: schoolRank.school_id ?? "",
              rank: index,
            })),
          },
        });
      } else {
        const result = await insertSchoolRank({
          variables: {
            delete_offers_where: deletedOffers,
            delete_waitlists_where: deletedWaitlists,
            deleted_school_ranks: deletedSchoolRanks,
            upserted_school_ranks: upsertedSchoolRanks,
          },
        });
        await triggerRuleExecutionsForSoftDeletedOffers({
          offers: result.data?.delete_offer?.returning ?? [],
          scope: "me",
        });
      }
      await refetchSchoolRanks();
    },
    [
      flags,
      formId,
      getDeleteSchoolsAfterGradesChange,
      getDeleteOffersAfterEligibilityChange,
      getDeleteWaitlistsAfterEligibilityChange,
      getDeletedOffers,
      getDeletedRanks,
      getDeletedWaitlists,
      getUpsertedRanks,
      insertSchoolRank,
      putAnswerForMe,
      refetchSchoolRanks,
      schoolRankingSection,
    ]
  );

  const deleteRankedSchools = useCallback(
    async (rankedSchools: readonly RankedSchool[], schoolsToRemove: uuid[]) => {
      const updatedSelectedSchoolIds = rankedSchools
        .filter((s) => !schoolsToRemove.find((id) => s.school.id === id))
        .map((s) => s.school.id);
      await setSchoolRanks(updatedSelectedSchoolIds, schoolsToRemove);

      const currentDeletedRankedSchools = deletedRankedSchoolsVar();
      deletedRankedSchoolsVar(
        currentDeletedRankedSchools.concat(schoolsToRemove)
      );
    },
    [setSchoolRanks]
  );

  return { deleteRankedSchools };
};
